// src/drawing/tools/RectangleTool.js
// Base class for 3-click rectangle drawing (field + mast rectangles).
// Includes a live "tape measure" that shows dimensions in meters
// as the user draws on the map.
import { createRectangleFrom3Points, projectPointOntoLine } from "../utils/geometry";
import { getUtmProj } from "../utils/projection";
import proj4 from "proj4";

export class RectangleTool {
  constructor(map, setGeometries, setWorkflowStep, firstABVectorRef = null) {
    this.map = map;
    this.setGeometries = setGeometries;
    this.setWorkflowStep = setWorkflowStep;

    this.points = [];
    this.fixedB_utm = null;
    this.firstABVector = null;
    this.firstUTM = null;

    this.firstABVectorRef = firstABVectorRef;

    // ── Tape measure label ─────────────────────────────────────────
    // A plain DOM div that follows the cursor, showing live distance
    // in meters while drawing. Simpler and more reliable than a
    // MapLibre Popup which can have z-index/anchor issues.
    this.measureLabel = document.createElement("div");
    this.measureLabel.style.cssText = `
      position: fixed;
      pointer-events: none;
      z-index: 9999;
      font-family: system-ui, sans-serif;
      font-size: 14px;
      font-weight: 600;
      color: #1e293b;
      background: rgba(255,255,255,0.95);
      padding: 4px 10px;
      border-radius: 4px;
      border: 2px solid #f97316;
      white-space: nowrap;
      display: none;
      box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    `;
    document.body.appendChild(this.measureLabel);

    // Track raw mouse position for label placement
    this._onRawMouseMove = (e) => {
      this.measureLabel.style.left = (e.clientX + 16) + "px";
      this.measureLabel.style.top = (e.clientY - 30) + "px";
    };
    this.map.getCanvas().addEventListener("mousemove", this._onRawMouseMove);
  }

  setReferenceVector(vec) {
    this.firstABVector = vec;
    if (this.firstABVectorRef) {
      this.firstABVectorRef.current = vec;
    }
  }

  setUTM(utm) {
    this.firstUTM = utm;
  }

  onClick(lngLat) {
    this.points.push([lngLat.lng, lngLat.lat]);
    if (this.points.length === 3) {
      this.finalizeRectangle();
    }
  }

  onMouseMove(lngLat) {
    if (this.points.length < 1) return;

    const wgs84 = "EPSG:4326";
    const A = this.points[0];
    const utm = this.firstUTM || getUtmProj(A);

    const A_utm = proj4(wgs84, utm, A);
    const mouse_utm = proj4(wgs84, utm, [lngLat.lng, lngLat.lat]);

    let B_utm = this.points.length === 1
      ? mouse_utm
      : proj4(wgs84, utm, this.points[1]);

    const B_ll = proj4(utm, wgs84, B_utm);
    this.updatePreviewLine([A, B_ll]);

    // ── Tape measure: show live distance in meters ────────────────
    if (this.points.length === 1) {
      // After 1st click: show length of AB edge (the line being drawn)
      const lengthM = Math.hypot(B_utm[0] - A_utm[0], B_utm[1] - A_utm[1]);
      this.showMeasurement(lngLat, `${lengthM.toFixed(1)} m`);
    }

    if (this.points.length === 2) {
      const rect = createRectangleFrom3Points(A_utm, B_utm, mouse_utm)
        .map((p) => proj4(utm, wgs84, p));
      this.updatePreviewRectangle(rect);

      // After 2nd click: show both length and width of the rectangle
      const lengthM = Math.hypot(B_utm[0] - A_utm[0], B_utm[1] - A_utm[1]);
      // Width = perpendicular distance from AB line to mouse position
      const AB_dx = B_utm[0] - A_utm[0];
      const AB_dy = B_utm[1] - A_utm[1];
      const AB_len = Math.hypot(AB_dx, AB_dy);
      // Normal vector of AB line
      const nx = -AB_dy / AB_len;
      const ny = AB_dx / AB_len;
      // Project AC onto normal to get perpendicular width
      const widthM = Math.abs(
        (mouse_utm[0] - A_utm[0]) * nx + (mouse_utm[1] - A_utm[1]) * ny
      );
      this.showMeasurement(lngLat, `${lengthM.toFixed(1)} × ${widthM.toFixed(1)} m`);
    }
  }

  // ── Show/hide tape measure label ─────────────────────────────────
  showMeasurement(lngLat, text) {
    this.measureLabel.textContent = text;
    this.measureLabel.style.display = "block";
  }

  hideMeasurement() {
    this.measureLabel.style.display = "none";
  }

  finalizeRectangle() {
    const wgs84 = "EPSG:4326";
    const A = this.points[0];
    const B = this.points[1];
    const C = this.points[2];
 
    const utm = getUtmProj(A);
    const A_utm = proj4(wgs84, utm, A);
    const B_utm = proj4(wgs84, utm, B);
    const C_utm = proj4(wgs84, utm, C);
 
    const dx = B_utm[0] - A_utm[0];
    const dy = B_utm[1] - A_utm[1];
    const length = Math.hypot(dx, dy);
    const AB = length ? [dx / length, dy / length] : null;

    this.setReferenceVector(AB);
    this.setUTM(utm);
 
    if (this.firstABVectorRef) {
      this.firstABVectorRef.current = AB;
    }

    const rect = createRectangleFrom3Points(A_utm, B_utm, C_utm)
      .map((p) => proj4(utm, wgs84, p));
 
    this.clearPreviews();
    return rect;
  }

  updatePreviewLine(coords) {
    const data = {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: { type: "LineString", coordinates: coords } }],
    };
    if (this.map.getSource("preview-line")) this.map.getSource("preview-line").setData(data);
    else {
      this.map.addSource("preview-line", { type: "geojson", data });
      this.map.addLayer({
        id: "preview-line",
        type: "line",
        source: "preview-line",
        paint: { "line-color": "orange", "line-width": 3 },
      });
    }
  }

  updatePreviewRectangle(coords) {
    const data = {
      type: "FeatureCollection",
      features: [{ type: "Feature", geometry: { type: "Polygon", coordinates: [coords] } }],
    };
    if (this.map.getSource("preview-rect")) this.map.getSource("preview-rect").setData(data);
    else {
      this.map.addSource("preview-rect", { type: "geojson", data });
      this.map.addLayer({
        id: "preview-rect",
        type: "fill",
        source: "preview-rect",
        paint: { "fill-color": "orange", "fill-opacity": 0.3 },
      });
    }
  }

  // ── Undo (Signify-Feedback Dennis 03.07.2026) ────────────────────
  // Nimmt den LETZTEN Klick beim Rechteck-Zeichnen zurück (Feld- und
  // Mast-Rechteck erben das). Gilt nur für unfertige Klicks — ein schon
  // fertiges Rechteck korrigiert man weiterhin über den Schritt-Button
  // in der Sidebar (goToStep), der alles Abhängige mit aufräumt.
  undoLastPoint() {
    if (this.points.length === 0) return false;
    this.points.pop();
    // fixedB_utm gehört zum 2. Klick (MastRectangleTool) — mit weg damit
    if (this.points.length < 2) this.fixedB_utm = null;
    this.clearPreviewGraphics();
    return true;
  }

  // Nur die Preview-GRAFIK leeren (Layer bleiben bestehen, points bleiben
  // erhalten) — beim nächsten Mousemove zeichnet sich die Preview neu.
  clearPreviewGraphics() {
    const empty = { type: "FeatureCollection", features: [] };
    ["preview-line", "preview-rect"].forEach((id) => {
      if (this.map.getSource(id)) this.map.getSource(id).setData(empty);
    });
    this.hideMeasurement();
  }

  clearPreviews() {
    ["preview-line", "preview-rect"].forEach((id) => {
      if (this.map.getSource(id)) {
        this.map.removeLayer(id);
        this.map.removeSource(id);
      }
    });
    // Hide the tape measure when drawing is done
    this.hideMeasurement();
    this.points = [];
    this.fixedB_utm = null;
  }
}